import { format, parseISO } from "date-fns"

export type trade_review_summary = Record<string, number>

export type scorecard_row = {
  retailer: string
  sales: number
  trade_pct: number
  events: number
  lift: number
  model: number
  model_lo: number
  model_hi: number
  per: number | null
  fill: number
  read: string
  why: string
}

export type pnl_row = {
  retailer: string
  sales: number
  units: number
  working: number
  fees: number
  placement: number
  fair_share: number
  retail_nw: number
  terms: number
  ops_prog: number
  net: number
  total_pct: number
  working_share: number
  net_unit: number
}

export type weekly_row = {
  retailer: string
  family: string
  week: string
  units: number
  ordered: number
  on_promo: number
  baseline: number | null
}

export type promo_window = {
  retailer: string
  family: string
  start: string
  end: string
  rate: number | null
  units: number | null
  billed: number | null
  source: string
}

export type event_row = {
  event: string
  retailer: string
  start: string
  end: string
  weeks: number
  rate: number | null
  groups: string
  base_wk: number
  promo_wk: number
  lift: number
  fill: number
  inc_rev: number
  spend: number | null
  spend_src: string
}

export type curve_row = { rel: number; idx: number; n: number }
export type depth_row = { band: string; events: number; lift: number; inc_rev: number; spend: number; per: number }
export type funnel_row = { step: string; bucket: string; amount: number; share: number; note: string }
export type calendar_month = { month: string; kehe_tpr_on_calendar: number; billed: number; billed_no_row: number }
export type plan_row = { key: string; promo: string; retailer: string; plan: number; billed: number; ratio: number }
export type rebuilt_row = { promo: string; retailer: string; start: string; end: string; products: string; rate: number | null; units: number; billed: number }
export type watch_row = { series: string; week: string; shipped: number; ordered: number }
export type fill_row = { family: string; ordered: number; shipped: number; short: number; fill: number }
export type kehe_cost_row = { cost: string; group: string; amount: number; pct_sales: number; driver: string }
export type dc_cost_row = { label: string; sales: number; shorts: number; fines: number; fuel: number; credits: number; ops_rate: number }
export type fine_month = { month: string; charges: number; amount: number }
export type oi_row = { source: string; checked: string; found: string }

export type trade_review = {
  summary: trade_review_summary
  retailer_scorecard: scorecard_row[]
  retailer_pnl: pnl_row[]
  weekly: weekly_row[]
  promo_windows: promo_window[]
  events: event_row[]
  event_curve: curve_row[]
  depth: depth_row[]
  match_funnel: funnel_row[]
  calendar_months: calendar_month[]
  plan_vs_billed: plan_row[]
  rebuilt_promos: rebuilt_row[]
  watch_weekly: watch_row[]
  fill_by_family: fill_row[]
  kehe_costs: kehe_cost_row[]
  dc_costs: dc_cost_row[]
  fines_810: fine_month[]
  oi_status: oi_row[]
}

export const ALL_PRODUCTS = "All products"
export const DEFAULT_RETAILER = "Sprouts"
export const DEFAULT_FAMILY = "MF Classics 8oz"
export const PLACEHOLDER_COGS_PCT = 60

const whole = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 })
const cents = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" })

export function fmt_int(value: number): string {
  return whole.format(value)
}

export function fmt_usd2(value: number): string {
  return cents.format(value)
}

export function fmt_pct0(value: number): string {
  return `${(value * 100).toFixed(0)}%`
}

export function fmt_pct1(value: number): string {
  return `${(value * 100).toFixed(1)}%`
}

export function fmt_signed_pct(value: number): string {
  const rounded = Math.round(Math.abs(value) * 100)
  return `${value >= 0 ? "+" : "−"}${rounded}%`
}

/** $3.61M, $138k, $4.2k, $350. The sign sits in front of the dollar. */
export function fmt_usd_short(value: number): string {
  const size = Math.abs(value)
  const sign = value < 0 ? "−" : ""
  if (size >= 1_000_000) return `${sign}$${(size / 1_000_000).toFixed(2)}M`
  if (size >= 10_000) return `${sign}$${(size / 1_000).toFixed(0)}k`
  if (size >= 1_000) return `${sign}$${(size / 1_000).toFixed(1)}k`
  return `${sign}$${size.toFixed(0)}`
}

export function fmt_day(day: string): string {
  return format(parseISO(day), "MMM d")
}

export function fmt_month(month: string): string {
  return format(parseISO(`${month}-01`), "MMM")
}

export function retailer_choices(weekly: weekly_row[]): string[] {
  return [...new Set(weekly.map((row: weekly_row) => row.retailer))]
}

/** All products first, then each family this retailer actually buys. */
export function family_choices(weekly: weekly_row[], retailer: string): string[] {
  const families = [...new Set(weekly.filter((row: weekly_row) => row.retailer === retailer).map((row: weekly_row) => row.family))]
  return families.sort((a: string, b: string) => {
    if (a === ALL_PRODUCTS) return -1
    if (b === ALL_PRODUCTS) return 1
    return a.localeCompare(b)
  })
}

/** Lift across the promo weeks in view, against those same weeks' baseline. Null with no promo weeks. */
export function view_lift(rows: weekly_row[]): { lift: number; weeks: number } | null {
  const promo = rows.filter((row: weekly_row) => row.on_promo === 1 && row.baseline != null)
  if (promo.length === 0) return null
  const units = promo.reduce((sum: number, row: weekly_row) => sum + row.units, 0)
  const baseline = promo.reduce((sum: number, row: weekly_row) => sum + (row.baseline ?? 0), 0)
  if (baseline <= 0) return null
  return { lift: units / baseline - 1, weeks: promo.length }
}

/** The chart's first and last week that a promo window touches, or null when it falls outside them. */
export function window_weeks(weeks: string[], start: string, end: string): { first: string; last: string } | null {
  const first_day = parseISO(start).getTime()
  const last_day = parseISO(end).getTime()
  const week_ms = 7 * 24 * 60 * 60 * 1000
  const touched = weeks.filter((week: string) => {
    const week_start = parseISO(week).getTime()
    return week_start <= last_day && week_start + week_ms > first_day
  })
  if (touched.length === 0) return null
  return { first: touched[0]!, last: touched[touched.length - 1]! }
}

/** Net after all costs less cost of goods, worked in cents. */
export function contribution(net: number, sales: number, cogs_pct: number): number {
  const net_cents = Math.round(net * 100)
  const sales_cents = Math.round(sales * 100)
  return (net_cents - Math.round((sales_cents * cogs_pct) / 100)) / 100
}

export function search_events(events: event_row[], query: string): event_row[] {
  const wanted = query.trim().toLowerCase()
  if (!wanted) return events
  return events.filter((row: event_row) => `${row.retailer} ${row.groups}`.toLowerCase().includes(wanted))
}

export function funnel_buckets(rows: funnel_row[]): { bucket: string; amount: number }[] {
  const totals = new Map<string, number>()
  rows.forEach((row: funnel_row) => totals.set(row.bucket, (totals.get(row.bucket) ?? 0) + row.amount))
  return [...totals].map(([bucket, amount]: [string, number]) => ({ bucket, amount }))
}

export function highest_cost_first(rows: pnl_row[]): pnl_row[] {
  return [...rows].sort((a: pnl_row, b: pnl_row) => b.total_pct - a.total_pct)
}
